<div mat-dialog-title>
  <div class="flex items-center justify-between h-48">
    <h6>
      {{ data?.repairerSite ? (data?.isEditRepairerSite ? 'Edit Repairer' : 'Re-Invite Repairer') : 'Add Repairer' }}
    </h6>
    <button matIconButton aria-label="Close" mat-dialog-close>
      <mat-icon>close</mat-icon>
    </button>
  </div>
</div>
<mat-divider class="mx-20!" />
<form
  [formGroup]="repairerForm"
  (ngSubmit)="
    data?.repairerSite ? (data.isEditRepairerSite ? editRepairerSite() : reinviteRepairerSite()) : addRepairerSite()
  ">
  <section mat-dialog-content>
    <div class="title-1 pt-5 text-dove-gray mb-10">Company Information</div>

    <article class="grid grid-cols-3 gap-20">
      <mat-form-field class="col-span-2">
        <mat-label>Company Name</mat-label>
        <input formControlName="companyName" matInput required />
        @if (companyName.hasError('required')) {
          <mat-error> Company Name is <strong>required</strong> </mat-error>
        }
      </mat-form-field>

      <mat-form-field>
        <mat-label>Repairer Group</mat-label>
        <mat-select formControlName="repairerGroupId">
          @for (group of data?.repairersGroup; track group) {
            <mat-option [value]="group.id">
              {{ group.name }}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
    </article>

    <article class="grid grid-cols-3 gap-x-20">
      <mat-form-field>
        <mat-label>Country</mat-label>
        <mat-select formControlName="country" required>
          @for (country of countries; track country) {
            <mat-option [value]="country.stateName">
              {{ country.abbreviation }}
            </mat-option>
          }
        </mat-select>
        @if (country.hasError('required')) {
          <mat-error> Country is <strong>required</strong> </mat-error>
        }
      </mat-form-field>

      <mat-form-field>
        <mat-label>{{ !country?.value || country?.value === 'Australia' ? 'ABN' : 'NZBN' }}</mat-label>
        <input formControlName="abn" matInput required (focusout)="adjustFormatOfAbn()" />
        @if (abn.hasError('required')) {
          <mat-error>
            {{ !country?.value || country?.value === 'Australia' ? 'ABN' : 'NZBN' }} is <strong>required</strong>
          </mat-error>
        }
        @if (!abn.valid && !abn.hasError('required')) {
          <mat-error class="mat-error"> Please enter a valid ABN </mat-error>
        }
      </mat-form-field>

      <mat-form-field>
        <mat-label>License Number</mat-label>
        <input formControlName="licenseNumber" matInput />
      </mat-form-field>

      <mat-form-field>
        <mat-label>License Start Date</mat-label>
        <input
          (keypress)="dateInputHelper.restrictDateInput($event)"
          [matDatepicker]="licenseRenewalDatePicker"
          formControlName="licenseRenewalDate"
          matInput />
        <mat-datepicker-toggle [for]="licenseRenewalDatePicker" matSuffix></mat-datepicker-toggle>
        <mat-datepicker #licenseRenewalDatePicker></mat-datepicker>
        @if (!licenseRenewalDate.valid) {
          <mat-error> The date format is <strong>DD/MM/YYYY</strong> </mat-error>
        }
      </mat-form-field>

      <mat-form-field>
        <mat-label>Quoting System</mat-label>
        <mat-select formControlName="quotingSystem">
          @for (item of quotingSystemList; track item) {
            <mat-option [value]="item">
              {{ item }}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
    </article>

    <article class="grid grid-cols-2 gap-x-20">
      <mat-form-field>
        <mat-label>Phone Number</mat-label>
        <input formControlName="phoneNumber" matInput required />
        @if (phoneNumber.hasError('required')) {
          <mat-error> Phone number is <b>required</b> </mat-error>
        }
        @if (phoneNumber.touched && phoneNumber.hasError('phoneInvalid')) {
          <mat-error> {{ getErrorMessage(phoneNumber.errors) }} </mat-error>
        }
      </mat-form-field>

      <mat-form-field>
        <mat-label>Email</mat-label>
        <input formControlName="emailCompany" matInput required />
        @if (emailCompany.invalid && emailCompany.hasError('required')) {
          <mat-error> Email is <strong>required</strong> </mat-error>
        }
        @if (emailCompany.invalid && emailCompany.hasError('pattern')) {
          <mat-error> Please enter a valid email address </mat-error>
        }
      </mat-form-field>

      <mat-form-field>
        <mat-label>Primary Accounts Email</mat-label>
        <input formControlName="primaryAccountsEmail" matInput required />
        @if (primaryAccountsEmail.invalid && primaryAccountsEmail.hasError('required')) {
          <mat-error> Email is <strong>required</strong> </mat-error>
        }
        @if (primaryAccountsEmail.invalid && primaryAccountsEmail.hasError('pattern')) {
          <mat-error> Please enter a valid email address </mat-error>
        }
      </mat-form-field>

      <mat-form-field>
        <mat-label>Secondary Accounts Email</mat-label>
        <input formControlName="secondaryAccountsEmail" matInput />
        @if (secondaryAccountsEmail.invalid && secondaryAccountsEmail.hasError('pattern')) {
          <mat-error> Please enter a valid email address </mat-error>
        }
      </mat-form-field>
    </article>

    <div class="title-1 pt-5 text-dove-gray mb-10">Repairer Administrator</div>

    <article class="grid grid-cols-3 gap-20">
      <mat-form-field>
        <mat-label>Username</mat-label>
        <input formControlName="username" matInput required />
        @if (username.hasError('required')) {
          <mat-error> Username is <strong>required</strong> </mat-error>
        }
        @if (!username.valid && !username.hasError('required')) {
          <mat-error> This username already exists </mat-error>
        }
      </mat-form-field>
      <mat-form-field>
        <mat-label>First Name</mat-label>
        <input formControlName="firstName" matInput required />
        @if (firstName.hasError('required')) {
          <mat-error> First Name is <strong>required</strong> </mat-error>
        }
      </mat-form-field>
      <mat-form-field>
        <mat-label>Last Name</mat-label>
        <input formControlName="lastName" matInput required />
        @if (lastName.hasError('required')) {
          <mat-error> Last Name is <strong>required</strong> </mat-error>
        }
      </mat-form-field>
    </article>

    <article class="grid grid-cols-2 gap-20">
      <mat-form-field>
        <mat-label>Email</mat-label>
        <input formControlName="emailRepairer" matInput required />
        @if (emailRepairer.hasError('required')) {
          <mat-error> Email is <strong>required</strong> </mat-error>
        }
        @if (!emailRepairer.valid && !emailRepairer.hasError('required')) {
          <mat-error class="mat-error"> Please enter a valid email address </mat-error>
        }
      </mat-form-field>
      <mat-form-field>
        <mat-label>Contact</mat-label>
        <input formControlName="phone" matInput />
        @if (phone.touched && phone.hasError('phoneInvalid')) {
          <mat-error> {{ getErrorMessage(phone.errors) }} </mat-error>
        }
      </mat-form-field>
    </article>

    @if (
      !data?.isEditRepairerSite ||
      (!!emailRepairer.value && emailRepairer.value !== data.repairerSite.mainContact.emailAddress)
    ) {
      <div class="flex items-center justify-center pb-5 text-dove-gray mb-20!">
        <mat-icon class="pr-5">info_outline</mat-icon>
        <span class="body-1">An email will be sent to this email address to complete repairer information.</span>
      </div>
    }
  </section>
  <mat-divider class="mx-20!" />
  <mat-dialog-actions>
    <button matButton="elevated" class="charcoal-button" type="button" mat-dialog-close>CANCEL</button>
    <button matButton="elevated" class="red-button" type="submit" [disabled]="repairerForm.invalid || pending()">
      {{ data?.repairerSite ? (data?.isEditRepairerSite ? 'SAVE' : 'RE-INVITE') : 'INVITE' }}
    </button>
  </mat-dialog-actions>
</form>
